/**
 * Where the reader last stopped in each manga's offline chapters.
 * Kept in localStorage: downloads live on this device only, so does the bookmark.
 */

export interface ReadingProgress {
  chapterNum: number;
  /** 0-based index of the image at the middle of the screen */
  pageIndex: number;
  /** How far into that image, 0..1 */
  pageOffset: number;
  totalPages: number;
  /** Reached the end of the chapter */
  finished: boolean;
  updatedAt: number;
}

const STORAGE_KEY = 'manga_offline_reading_progress_v1';

function readAll(): Record<string, ReadingProgress> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

export function getAllReadingProgress(): Record<string, ReadingProgress> {
  return readAll();
}

export function getReadingProgress(mangaId: string): ReadingProgress | null {
  return readAll()[mangaId] || null;
}

export function saveReadingProgress(mangaId: string, progress: Omit<ReadingProgress, 'updatedAt'>): void {
  try {
    const all = readAll();
    all[mangaId] = { ...progress, updatedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Storage full or blocked: losing the bookmark is harmless
  }
}

/**
 * Which downloaded chapter to open next, and whether to jump back into it mid-way.
 * Unfinished chapter -> resume it; finished -> the next downloaded one;
 * nothing saved -> first downloaded chapter after what the card says was read.
 */
export function pickResumeChapter(
  downloaded: number[],
  progress: ReadingProgress | null,
  readCh: number
): { chapterNum: number; resume: boolean } | null {
  if (downloaded.length === 0) return null;
  const sorted = [...downloaded].sort((a, b) => a - b);

  if (progress && sorted.includes(progress.chapterNum)) {
    if (!progress.finished) return { chapterNum: progress.chapterNum, resume: true };
    const next = sorted.find((n) => n > progress.chapterNum);
    if (next != null) return { chapterNum: next, resume: false };
    return { chapterNum: progress.chapterNum, resume: false };
  }

  const firstUnread = sorted.find((n) => n > readCh);
  return { chapterNum: firstUnread ?? sorted[sorted.length - 1], resume: false };
}
